import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Map, Grid, Search, ChevronDown } from 'lucide-react';
import { Venue, City, Therapy, NearbyVenueSearchResponse } from '../types';
import { api } from '../services/api';
import { VenueCard } from '../components/VenueCard';
import { DIRECTORY_NAME, SITE_ORIGIN } from '../lib/brand';
import { buildDirectoryPath, buildListingPath } from '../lib/routes';
import {
  LONDON_AREA_ORDER,
  getVenueLocationFilterLabel,
  inferLondonArea,
  locationLabelToParam,
} from '../lib/londonAreas';
import { getPublicVenueTherapySlug } from '../lib/publicTherapies';
import {
  AMENITY_FILTER_CONFIG,
  getPriceBand,
  isDeprioritizedLeadVenue,
  matchesAmenityFilter,
  NEARBY_RADIUS_OPTIONS,
  poundCount,
  PRICE_FILTER_LABELS,
  SORT_OPTION_LABELS,
  toggleAmenityFilter,
  VALID_AMENITY_FILTERS,
  VALID_PRICE_FILTERS,
} from '../lib/listingFilters';
import type { AmenityFilter, PriceFilter, SortOption } from '../lib/listingFilters';

const LazyVenueMap = React.lazy(() =>
  import('../components/VenueMap').then((module) => ({ default: module.VenueMap })),
);

const LEGACY_LONDON_AREAS: Record<string, 'Central London' | 'West London' | 'East London' | 'North London' | 'South London'> = {
  'central-london': 'Central London',
  'west-london': 'West London',
  'east-london': 'East London',
  'north-london': 'North London',
  'south-london': 'South London',
};

function ListingNewsletterBlock() {
  const [email, setEmail] = React.useState('');
  const [status, setStatus] = React.useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('loading');
    try {
      const success = await api.subscribeToNewsletter(email);
      setStatus(success ? 'success' : 'error');
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className="section-surface-green relative my-8 overflow-hidden rounded-[1.75rem] border border-brand-sage-green/16 px-5 py-7 text-brand-charcoal shadow-[0_20px_50px_rgba(26,26,26,0.06)] sm:rounded-[2rem] sm:px-6 sm:py-8">
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(135deg,rgba(255,255,255,0.5),rgba(255,255,255,0.14))]" />
      <div className="pointer-events-none absolute -right-12 top-0 h-40 w-40 rounded-full bg-brand-sage-green/14 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-12 left-0 h-36 w-36 rounded-full bg-brand-sage-green/10 blur-3xl" />

      <div className="relative flex flex-col items-center justify-between gap-6 sm:flex-row">
        <div className="text-center sm:text-left">
          <p className="mb-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-[#6D7D5C]">The Recovery Digest</p>
          <h3 className="mb-1 text-lg font-serif leading-snug text-brand-charcoal">
            Discover new saunas, cold plunges,<br className="hidden sm:block" /> and recovery spots in the directory.
          </h3>
          <p className="text-sm text-brand-charcoal/58">One email. Once a week. No spam. Just good heat.</p>
        </div>

        <div className="w-full flex-shrink-0 sm:w-auto">
          {status === 'success' ? (
            <p className="whitespace-nowrap text-sm font-semibold text-[#6D7D5C]">You're in ✓</p>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col gap-2 sm:flex-row">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="flex-1 rounded-xl border border-brand-charcoal/10 bg-white/72 px-3 py-2.5 text-sm text-brand-charcoal placeholder:text-brand-charcoal/34 focus:outline-none focus:border-brand-sage-green/50"
              />
              <button
                type="submit"
                disabled={status === 'loading'}
                className="flex items-center justify-center gap-1.5 rounded-xl bg-brand-charcoal px-4 py-2.5 text-sm font-semibold text-brand-offwhite transition-opacity hover:opacity-90 disabled:opacity-50 whitespace-nowrap sm:flex-shrink-0"
              >
                {status === 'loading' ? 'Joining...' : 'Join Free'}
              </button>
            </form>
          )}
          {status === 'error' && <p className="mt-2 text-xs text-brand-charcoal/55">Something went wrong.</p>}
          {status !== 'success' && (
            <p className="mt-2 text-center text-[10px] text-brand-charcoal/36 sm:text-left">Join hundreds of recovery enthusiasts</p>
          )}
        </div>
      </div>
    </div>
  );
}

function ListingCardSkeleton() {
  return (
    <div className="overflow-hidden rounded-[1.6rem] border border-brand-charcoal/8 bg-white/72 shadow-[0_12px_30px_rgba(26,26,26,0.04)]">
      <div className="aspect-[16/10] animate-pulse bg-brand-charcoal/[0.06]" />
      <div className="space-y-3 p-5">
        <div className="h-3 w-24 animate-pulse rounded-full bg-brand-charcoal/[0.08]" />
        <div className="h-6 w-3/4 animate-pulse rounded-full bg-brand-charcoal/[0.08]" />
        <div className="h-4 w-full animate-pulse rounded-full bg-brand-charcoal/[0.06]" />
        <div className="h-4 w-5/6 animate-pulse rounded-full bg-brand-charcoal/[0.06]" />
        <div className="flex gap-2 pt-2">
          <div className="h-10 flex-1 animate-pulse rounded-[0.9rem] bg-brand-charcoal/[0.06]" />
          <div className="h-10 flex-1 animate-pulse rounded-[0.9rem] bg-brand-charcoal/[0.06]" />
        </div>
      </div>
    </div>
  );
}

export const ListingPage = () => {
  const { city, therapy } = useParams<{ city: string; therapy: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [viewMode, setViewMode] = useState<'grid' | 'map'>('grid');
  const [venues, setVenues] = useState<Venue[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [therapies, setTherapies] = useState<Therapy[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [sortBy, setSortBy] = useState<SortOption>('recommended');
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [shouldRenderMap, setShouldRenderMap] = useState(false);
  const hoverTimerRef = useRef<number | null>(null);
  const filterBarRef = useRef<HTMLDivElement | null>(null);
  const listingsColumnRef = useRef<HTMLDivElement | null>(null);
  const [desktopFilterBarHeight, setDesktopFilterBarHeight] = useState(88);
  const [listingsColumnHeight, setListingsColumnHeight] = useState(0);
  const selectedAreaParam = searchParams.get('area') ?? 'all';
  const postcodeQuery = (searchParams.get('postcode') ?? '').trim();
  const nearbyRadius = useMemo(() => {
    const value = Number.parseInt(searchParams.get('radius') ?? '10', 10);
    return NEARBY_RADIUS_OPTIONS.includes(value as typeof NEARBY_RADIUS_OPTIONS[number]) ? value as typeof NEARBY_RADIUS_OPTIONS[number] : 10;
  }, [searchParams]);
  const priceFilter = useMemo<PriceFilter>(() => {
    const value = searchParams.get('price');
    return value && VALID_PRICE_FILTERS.has(value as PriceFilter) ? (value as PriceFilter) : 'all';
  }, [searchParams]);
  const selectedAmenityFilters = useMemo<AmenityFilter[]>(() => {
    const rawFilters = (searchParams.get('amenities') ?? '')
      .split(',')
      .map((value) => value.trim().toLowerCase())
      .filter((value): value is AmenityFilter => VALID_AMENITY_FILTERS.has(value as AmenityFilter));
    return Array.from(new Set(rawFilters));
  }, [searchParams]);
  const requiredTherapies = useMemo(
    () =>
      (searchParams.get('must') ?? '')
        .split(',')
        .map((value) => value.trim().toLowerCase())
        .filter((value): value is string => Boolean(value)),
    [searchParams],
  );
  const curatedVenueSlugs = useMemo(
    () =>
      (searchParams.get('venues') ?? '')
        .split(',')
        .map((value) => value.trim().toLowerCase())
        .filter((value): value is string => Boolean(value)),
    [searchParams],
  );
  const [postcodeInput, setPostcodeInput] = useState(postcodeQuery);
  const [nearbySearch, setNearbySearch] = useState<NearbyVenueSearchResponse | null>(null);
  const [nearbyError, setNearbyError] = useState<string | null>(null);
  const isNearbySearchActive = postcodeQuery.length > 0;

  useEffect(() => {
    setPostcodeInput(postcodeQuery);
  }, [postcodeQuery]);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setNearbyError(null);
      try {
        const [citiesData, therapiesData, primaryData] = await Promise.all([
          api.getCities(),
          api.getTherapies(),
          isNearbySearchActive
            ? api.getNearbyVenues(postcodeQuery, nearbyRadius, therapy)
            : api.getVenues(city, therapy),
        ]);

        const nextNearby = isNearbySearchActive ? primaryData as NearbyVenueSearchResponse : null;
        setNearbySearch(nextNearby);
        setVenues(nextNearby ? nextNearby.venues : primaryData as Venue[]);
        setCities(citiesData);
        setTherapies(therapiesData);
      } catch (error) {
        console.error('Error fetching data:', error);
        setNearbySearch(null);
        setVenues([]);
        setNearbyError(error instanceof Error ? error.message : 'Something went wrong.');
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [city, therapy, isNearbySearchActive, postcodeQuery, nearbyRadius]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const isDesktop = window.innerWidth >= 1024;
    const shouldEnable = viewMode === 'map' || isDesktop;

    if (!shouldEnable) {
      setShouldRenderMap(false);
      return;
    }

    const delay = viewMode === 'map' ? 0 : 200;
    const timer = window.setTimeout(() => {
      setShouldRenderMap(true);
    }, delay);

    return () => {
      window.clearTimeout(timer);
    };
  }, [viewMode, city, therapy]);

  useEffect(() => {
    return () => {
      if (hoverTimerRef.current !== null) {
        window.clearTimeout(hoverTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const node = filterBarRef.current;
    if (!node) return;

    const updateHeight = () => {
      if (window.innerWidth >= 1024) {
        setDesktopFilterBarHeight(node.offsetHeight || 88);
      }
    };

    updateHeight();

    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updateHeight) : null;
    observer?.observe(node);
    window.addEventListener('resize', updateHeight);

    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', updateHeight);
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const node = listingsColumnRef.current;
    if (!node) return;

    const updateHeight = () => {
      if (window.innerWidth >= 1024) {
        setListingsColumnHeight(node.offsetHeight || 0);
      } else {
        setListingsColumnHeight(0);
      }
    };

    updateHeight();

    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updateHeight) : null;
    observer?.observe(node);
    window.addEventListener('resize', updateHeight);

    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', updateHeight);
    };
  }, []);

  const updateListingSearchParams = (updater: (next: URLSearchParams) => void) => {
    const next = new URLSearchParams(searchParams);
    updater(next);
    setSearchParams(next, { replace: true });
  };

  const submitPostcodeSearch = () => {
    const normalized = postcodeInput.trim().toUpperCase();

    updateListingSearchParams((next) => {
      if (!normalized) {
        next.delete('postcode');
        next.delete('radius');
        return;
      }

      next.set('postcode', normalized);
      next.set('radius', String(nearbyRadius));
    });
  };

  const clearPostcodeSearch = () => {
    setPostcodeInput('');
    setNearbySearch(null);
    setNearbyError(null);
    updateListingSearchParams((next) => {
      next.delete('postcode');
      next.delete('radius');
    });
  };

  const navigateToListing = (nextCity: string, nextTherapy: string) => {
    const nextSearch = new URLSearchParams(searchParams);
    const normalizedCity = nextCity.trim().toLowerCase();

    if (normalizedCity !== 'london') {
      nextSearch.delete('area');
    }

    const search = nextSearch.toString();
    navigate({
      pathname: buildListingPath(nextCity, nextTherapy),
      search: search ? `?${search}` : '',
    });
  };

  const handleCardMouseEnter = (venueId: string) => {
    if (hoverTimerRef.current !== null) {
      window.clearTimeout(hoverTimerRef.current);
    }

    hoverTimerRef.current = window.setTimeout(() => {
      setHoveredId(venueId);
    }, 120);
  };

  const handleCardMouseLeave = () => {
    if (hoverTimerRef.current !== null) {
      window.clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    setHoveredId(null);
  };

  const baseVenues = useMemo(() => {
    let result = verifiedOnly ? venues.filter(v => v.verified) : [...venues];

    if (curatedVenueSlugs.length > 0) {
      const curatedSlugSet = new Set(curatedVenueSlugs);
      result = result.filter((venue) => curatedSlugSet.has(venue.slug.toLowerCase()));
    }

    if (requiredTherapies.length > 0) {
      result = result.filter((venue) => {
        const venueTherapies = venue.therapy.map((item) => item.toLowerCase());
        return requiredTherapies.every((required) => venueTherapies.includes(required));
      });
    }
    return result;
  }, [venues, verifiedOnly, requiredTherapies, curatedVenueSlugs]);

  const isLondonPage = (city ?? '').toLowerCase() === 'london';
  const availableLocationOptions = useMemo(
    () =>
      isLondonPage
        ? LONDON_AREA_ORDER.filter((area) =>
            baseVenues.some((venue) =>
              inferLondonArea({
                neighborhood: venue.neighborhood,
                address: venue.address,
                slug: venue.slug,
                description: venue.description,
              }) === area,
            ),
          )
        : [],
    [baseVenues, isLondonPage],
  );
  const selectedAreaLabel = useMemo(() => {
    if (selectedAreaParam === 'all') return 'all';
    if (LEGACY_LONDON_AREAS[selectedAreaParam]) return LEGACY_LONDON_AREAS[selectedAreaParam];
    return availableLocationOptions.find((location) => locationLabelToParam(location) === selectedAreaParam) ?? selectedAreaParam;
  }, [availableLocationOptions, selectedAreaParam]);
  const handleAreaChange = (area: 'all' | string) => {
    updateListingSearchParams((next) => {
      if (area === 'all') next.delete('area');
      else next.set('area', locationLabelToParam(area));
    });
  };
  const handlePriceFilterChange = (nextPrice: PriceFilter) => {
    updateListingSearchParams((next) => {
      if (nextPrice === 'all') next.delete('price');
      else next.set('price', nextPrice);
    });
  };
  const handleAmenityToggle = (key: AmenityFilter) => {
    updateListingSearchParams((next) => {
      const current = (next.get('amenities') ?? '')
        .split(',')
        .map((value) => value.trim().toLowerCase())
        .filter((value): value is AmenityFilter => VALID_AMENITY_FILTERS.has(value as AmenityFilter));
      const ordered = toggleAmenityFilter(current, key);
      if (ordered.length === 0) {
        next.delete('amenities');
        return;
      }
      next.set('amenities', ordered.join(','));
    });
  };
  const locationFilteredVenues = useMemo(() => {
    if (!isLondonPage || selectedAreaParam === 'all') return baseVenues;

    const legacyArea = LEGACY_LONDON_AREAS[selectedAreaParam];
    const selectedBroadArea =
      legacyArea
      ?? LONDON_AREA_ORDER.find((area) => locationLabelToParam(area) === selectedAreaParam);

    if (selectedBroadArea) {
      return baseVenues.filter((venue) =>
        inferLondonArea({
          neighborhood: venue.neighborhood,
          address: venue.address,
          slug: venue.slug,
          description: venue.description,
        }) === selectedBroadArea,
      );
    }

    return baseVenues.filter((venue) => {
      const locationLabel = getVenueLocationFilterLabel(venue.area, venue.neighborhood);
      return locationLabel ? locationLabelToParam(locationLabel) === selectedAreaParam : false;
    });
  }, [baseVenues, isLondonPage, selectedAreaParam]);

  const priceFilterCounts = useMemo<Record<Exclude<PriceFilter, 'all'>, number>>(() => {
    const counts: Record<Exclude<PriceFilter, 'all'>, number> = {
      budget: 0,
      'mid-range': 0,
      premium: 0,
      luxury: 0,
    };

    locationFilteredVenues.forEach((venue) => {
      const band = getPriceBand(venue.priceRange);
      if (band) counts[band] += 1;
    });

    return counts;
  }, [locationFilteredVenues]);

  const amenityFilterCounts = useMemo<Record<AmenityFilter, number>>(() => {
    const counts: Record<AmenityFilter, number> = {
      private: 0,
      membership: 0,
      accessible: 0,
      parking: 0,
    };

    locationFilteredVenues.forEach((venue) => {
      AMENITY_FILTER_CONFIG.forEach(({ key }) => {
        if (matchesAmenityFilter(venue, key)) {
          counts[key] += 1;
        }
      });
    });

    return counts;
  }, [locationFilteredVenues]);

  const filteredVenues = useMemo(() => {
    let result = [...locationFilteredVenues];

    if (priceFilter !== 'all') {
      result = result.filter((venue) => getPriceBand(venue.priceRange) === priceFilter);
    }

    if (selectedAmenityFilters.length > 0) {
      result = result.filter((venue) =>
        selectedAmenityFilters.every((filter) => matchesAmenityFilter(venue, filter)),
      );
    }

    switch (sortBy) {
      case 'rating':
        result.sort((a, b) => b.rating - a.rating);
        break;
      case 'price-asc':
        result.sort((a, b) => poundCount(a.priceRange) - poundCount(b.priceRange));
        break;
      case 'price-desc':
        result.sort((a, b) => poundCount(b.priceRange) - poundCount(a.priceRange));
        break;
      default:
        result.sort((a, b) => {
          const aScore = (a.tier === 'premium' ? 2 : 0) + (a.verified ? 1 : 0);
          const bScore = (b.tier === 'premium' ? 2 : 0) + (b.verified ? 1 : 0);
          if (aScore !== bScore) return bScore - aScore;
          return b.rating - a.rating;
        });
    }

    result.sort((a, b) => {
      const aDeprioritized = isDeprioritizedLeadVenue(a);
      const bDeprioritized = isDeprioritizedLeadVenue(b);
      if (aDeprioritized === bDeprioritized) return 0;
      return aDeprioritized ? 1 : -1;
    });

    return result;
  }, [locationFilteredVenues, priceFilter, selectedAmenityFilters, sortBy]);

  const isDirectoryRoute = location.pathname === buildDirectoryPath();
  const isAllCities = isDirectoryRoute || city?.toLowerCase() === 'therapies' || city?.toLowerCase() === 'all' || !city;
  const isAllTherapies = therapy?.toLowerCase() === 'all' || !therapy;

  const cityName = isAllCities ? 'all locations' : (cities.find(c => c.slug === city)?.name || city);

  const therapyName = therapies.find(t => t.slug === therapy)?.name ||
    (therapy && !isAllTherapies
      ? therapy.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
      : 'Saunas & Cold Plunges');

  const displayTherapyName = isAllTherapies ? 'Saunas & Cold Plunges' : therapyName;

  const isDirectoryIndex = isAllCities && isAllTherapies;
  const hasActiveListingFilters =
    isNearbySearchActive
    || verifiedOnly
    || selectedAreaParam !== 'all'
    || priceFilter !== 'all'
    || selectedAmenityFilters.length > 0
    || requiredTherapies.length > 0
    || curatedVenueSlugs.length > 0;
  const activeFilterCount =
    (isNearbySearchActive ? 1 : 0)
    + (verifiedOnly ? 1 : 0)
    + (selectedAreaParam !== 'all' ? 1 : 0)
    + (priceFilter !== 'all' ? 1 : 0)
    + selectedAmenityFilters.length
    + requiredTherapies.length
    + (curatedVenueSlugs.length > 0 ? 1 : 0);
  const secondaryFilterCount =
    (verifiedOnly ? 1 : 0)
    + (selectedAreaParam !== 'all' ? 1 : 0)
    + (priceFilter !== 'all' ? 1 : 0)
    + selectedAmenityFilters.length
    + (!isAllCities ? 1 : 0)
    + (sortBy !== 'recommended' ? 1 : 0);
  const clearAllListingFilters = () => {
    setVerifiedOnly(false);
    setPostcodeInput('');
    setNearbySearch(null);
    setNearbyError(null);
    updateListingSearchParams((next) => {
      next.delete('area');
      next.delete('price');
      next.delete('amenities');
      next.delete('must');
      next.delete('venues');
      next.delete('postcode');
      next.delete('radius');
    });
  };
  const desktopMapTop = `calc(4rem + ${desktopFilterBarHeight}px + 1.5rem)`;
  const desktopMapHeight = `calc((100vh - (4rem + ${desktopFilterBarHeight}px) - 3rem) * 0.9)`;
  const pageTitle = isDirectoryIndex
    ? `All Saunas & Cold Plunges | ${DIRECTORY_NAME}`
    : isAllTherapies
    ? `Saunas & Cold Plunges in ${cityName} | ${DIRECTORY_NAME}`
    : `${displayTherapyName} in ${cityName} | ${DIRECTORY_NAME}`;
  const metaDesc = isDirectoryIndex
    ? `Browse the full directory of saunas, ice baths and cold plunges across all live locations. Compare traditional sauna, infrared sauna, ice bath and contrast options.`
    : isAllTherapies
    ? `Browse curated saunas, ice baths and cold plunge spots in ${cityName}. Compare heat, cold and contrast options in one place.`
    : `Find the best ${displayTherapyName.toLowerCase()} in ${cityName}. Browse ${venues.length > 0 ? venues.length + ' ' : ''}verified venues, compare prices and book your session.`;

  const CANONICAL_ORIGIN = SITE_ORIGIN;
  const canonicalPath = isDirectoryIndex
    ? buildDirectoryPath()
    : buildListingPath(isAllCities ? 'all' : city, isAllTherapies ? 'all' : therapy);
  const canonicalUrl = `${CANONICAL_ORIGIN}${canonicalPath}`;

  const featuredVenues = filteredVenues
    .filter((venue) => venue.tier === 'premium' && !isDeprioritizedLeadVenue(venue))
    .slice(0, 3);
  const featuredVenueIds = new Set(featuredVenues.map((venue) => venue.id));
  const standardVenues = filteredVenues.filter((venue) => !featuredVenueIds.has(venue.id));

  const breadcrumbJsonLd = (() => {
    const items: Array<{ '@type': 'ListItem'; position: number; name: string; item: string }> = [];
    items.push({ '@type': 'ListItem', position: 1, name: 'Home', item: `${CANONICAL_ORIGIN}/` });

    const isAllAll = canonicalPath === buildDirectoryPath();
    if (isAllAll) {
      items.push({ '@type': 'ListItem', position: 2, name: 'Directory', item: canonicalUrl });
    } else if (isAllCities) {
      items.push({ '@type': 'ListItem', position: 2, name: 'Directory', item: `${CANONICAL_ORIGIN}${buildDirectoryPath()}` });
      items.push({ '@type': 'ListItem', position: 3, name: `${displayTherapyName} in all locations`, item: canonicalUrl });
    } else {
      items.push({ '@type': 'ListItem', position: 2, name: cityName, item: `${CANONICAL_ORIGIN}${buildListingPath(city, 'all')}` });
      items.push({ '@type': 'ListItem', position: 3, name: displayTherapyName, item: canonicalUrl });
    }

    return {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: items,
    };
  })();

  const itemListJsonLd = (() => {
    if (filteredVenues.length === 0) return null;

    const venueUrls = filteredVenues.slice(0, 50).map((v, idx) => {
      const citySlug = isAllCities ? v.city : (city ?? v.city);
      const therapySlug = isAllTherapies ? getPublicVenueTherapySlug(v.therapy, v.primaryTherapy) : (therapy ?? 'all');
      const venueCity = cities.find(c => c.slug === v.city)?.name ?? v.city;
      return {
        '@type': 'ListItem',
        position: idx + 1,
        name: v.name,
        url: `${CANONICAL_ORIGIN}/${citySlug}/${therapySlug}/${v.slug}`,
        image: v.image || undefined,
        address: v.address
          ? {
            '@type': 'PostalAddress',
            streetAddress: v.address,
            addressLocality: venueCity,
            addressCountry: 'GB',
          }
          : undefined,
      };
    });

    return {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      itemListElement: venueUrls,
    };
  })();

  return (
    <div className="w-full pt-[72px]">
      <title>{pageTitle}</title>
      <link rel="canonical" href={canonicalUrl} />
      <meta name="description" content={metaDesc} />
      <meta
        name="robots"
        content={isNearbySearchActive ? 'noindex,follow,max-image-preview:large' : 'index,follow,max-image-preview:large'}
      />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={metaDesc} />
      <meta property="og:type" content="website" />
      <meta name="twitter:card" content="summary" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }} />
      {itemListJsonLd ? (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(itemListJsonLd) }} />
      ) : null}

      {/* Sticky filter bar */}
      <div
        ref={filterBarRef}
        className="relative sticky top-[72px] z-30 overflow-hidden border-b border-brand-charcoal/5 backdrop-blur-sm"
        style={{
          backgroundColor: 'rgba(249, 248, 246, 0.92)',
          backgroundImage: 'linear-gradient(180deg, rgba(249,248,246,0.92) 0%, rgba(249,248,246,0.96) 100%)',
        }}
      >
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(196,105,79,0.08),transparent_24%),radial-gradient(circle_at_right,rgba(163,177,138,0.08),transparent_22%)]" />
        <div className="relative px-4 sm:px-6">
          <div className="site-shell pb-4 pt-5.5 lg:py-4">
            <div className="flex flex-col gap-2.5 lg:gap-3">
              <div className="flex flex-col gap-2.5 xl:flex-row xl:items-center xl:justify-between">
                <form
                  onSubmit={(event) => {
                    event.preventDefault();
                    submitPostcodeSearch();
                  }}
                  className="flex min-w-0 w-full flex-nowrap items-center gap-2 overflow-x-auto pb-1 pr-1 no-scrollbar snap-x snap-mandatory sm:grid sm:grid-cols-[minmax(14rem,1.15fr)_6.5rem_minmax(10.5rem,12rem)_auto_auto] sm:gap-2.5 sm:overflow-visible sm:pb-0 sm:pr-0 sm:snap-none xl:max-w-[58rem]"
                >
                  <label className="flex h-10 min-w-[12.5rem] shrink-0 snap-start items-center rounded-[0.95rem] border border-brand-charcoal/10 bg-white/88 px-3 shadow-[0_8px_18px_rgba(26,26,26,0.03)] sm:min-w-0 sm:shrink">
                    <span className="text-[0.95rem] leading-none" aria-hidden="true">📍</span>
                    <input
                      type="text"
                      inputMode="text"
                      autoComplete="postal-code"
                      placeholder="Enter a UK postcode"
                      value={postcodeInput}
                      onChange={(event) => setPostcodeInput(event.target.value)}
                      className="h-10 w-full bg-transparent px-3 text-sm font-medium text-brand-charcoal placeholder:text-brand-charcoal/34 focus:outline-none"
                    />
                  </label>

                  <div className="relative h-10 min-w-[5.5rem] shrink-0 snap-start sm:min-w-0 sm:shrink">
                    <select
                      value={nearbyRadius}
                      onChange={(event) => {
                        const nextRadius = Number.parseInt(event.target.value, 10);
                        updateListingSearchParams((next) => {
                          next.set('radius', String(nextRadius));
                          if (postcodeQuery) next.set('postcode', postcodeQuery);
                        });
                      }}
                      className="h-10 w-full appearance-none rounded-[0.95rem] border border-brand-charcoal/10 bg-white/88 pl-3 pr-8 text-sm font-medium text-brand-charcoal/70 focus:outline-none cursor-pointer"
                    >
                      {NEARBY_RADIUS_OPTIONS.map((radius) => (
                        <option key={radius} value={radius}>{radius} mi</option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-brand-charcoal/40 pointer-events-none" />
                  </div>

                  <div className="relative h-10 min-w-[9.5rem] flex-none shrink-0 snap-start sm:min-w-0 sm:flex-1">
                    <select
                      value={isAllTherapies ? 'all' : (therapy ?? 'all')}
                      onChange={(event) => {
                        const nextTherapy = event.target.value || 'all';
                        const currentCity = isAllCities ? 'all' : (city ?? 'all');
                        navigateToListing(currentCity, nextTherapy);
                      }}
                      className="h-10 w-full appearance-none rounded-[0.95rem] border border-brand-charcoal/10 bg-white/88 pl-3 pr-8 text-sm font-medium text-brand-charcoal/70 shadow-[0_8px_18px_rgba(26,26,26,0.03)] focus:outline-none cursor-pointer"
                    >
                      <option value="all">All therapies</option>
                      {therapies.map((item) => (
                        <option key={item.slug} value={item.slug}>{item.name}</option>
                      ))}
                    </select>
                    <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-brand-charcoal/40" />
                  </div>

                  <div className="flex shrink-0 snap-start gap-2">
                    <button
                      type="submit"
                      className="inline-flex h-10 items-center justify-center rounded-[0.95rem] bg-brand-charcoal px-4 text-sm font-semibold text-white transition-opacity hover:opacity-92"
                    >
                      Search
                    </button>
                    {isNearbySearchActive ? (
                      <button
                        type="button"
                        onClick={clearPostcodeSearch}
                        aria-label="Clear nearby search"
                        className="inline-flex h-10 items-center justify-center rounded-[0.95rem] border border-brand-charcoal/10 bg-white/88 px-3 text-sm font-semibold text-brand-charcoal/62 transition-colors hover:text-brand-charcoal"
                      >
                        Clear
                      </button>
                    ) : null}
                  </div>
                </form>

                <div className="flex items-center gap-2 lg:hidden">
                  <div className="flex min-w-0 flex-1 rounded-xl border border-brand-charcoal/10 bg-brand-offwhite p-1">
                    <button
                      onClick={() => setViewMode('grid')}
                      className={`inline-flex flex-1 items-center justify-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition-all ${
                        viewMode === 'grid' ? 'bg-brand-charcoal text-white' : 'text-brand-charcoal/50 hover:text-brand-charcoal'
                      }`}
                    >
                      <Grid className="w-4 h-4" />
                      List
                    </button>
                    <button
                      onClick={() => setViewMode('map')}
                      className={`inline-flex flex-1 items-center justify-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition-all ${
                        viewMode === 'map' ? 'bg-brand-charcoal text-white' : 'text-brand-charcoal/50 hover:text-brand-charcoal'
                      }`}
                    >
                      <Map className="w-4 h-4" />
                      Map
                    </button>
                  </div>
                </div>
              </div>

              {isNearbySearchActive && nearbySearch ? (
                <p className="text-sm text-brand-charcoal/56">
                  {filteredVenues.length} {filteredVenues.length === 1 ? 'result' : 'results'} within {nearbySearch.radiusMiles} miles of {nearbySearch.formattedPostcode}
                </p>
              ) : null}

              <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex items-center gap-2 overflow-x-auto pb-1 pr-1 no-scrollbar">
                  <button
                    type="button"
                    onClick={() => setVerifiedOnly((value) => !value)}
                    className={`rounded-full border px-3 py-2 text-sm font-medium transition-colors ${
                      verifiedOnly
                        ? 'border-brand-sage/30 bg-brand-sage/10 text-brand-sage'
                        : 'border-brand-charcoal/10 bg-white/78 text-brand-charcoal/62'
                    }`}
                  >
                    Verified
                  </button>
                  {Object.entries(PRICE_FILTER_LABELS).map(([value, label]) => {
                    const typedValue = value as Exclude<PriceFilter, 'all'>;
                    if (priceFilterCounts[typedValue] === 0 && priceFilter !== typedValue) return null;

                    return (
                      <button
                        key={typedValue}
                        type="button"
                        onClick={() => handlePriceFilterChange(priceFilter === typedValue ? 'all' : typedValue)}
                        className={`rounded-full border px-3 py-2 text-sm font-medium transition-colors ${
                          priceFilter === typedValue
                            ? 'border-brand-charcoal bg-brand-charcoal text-white'
                            : 'border-brand-charcoal/10 bg-white/78 text-brand-charcoal/62'
                        }`}
                      >
                        {label}
                      </button>
                    );
                  })}
                  {isLondonPage ? (
                    <>
                      <button
                        type="button"
                        onClick={() => handleAreaChange('all')}
                        className={`rounded-full border px-3 py-2 text-sm font-medium transition-colors ${
                          selectedAreaParam === 'all'
                            ? 'border-brand-charcoal bg-brand-charcoal text-white'
                            : 'border-brand-charcoal/10 bg-white/78 text-brand-charcoal/62'
                        }`}
                      >
                        All London
                      </button>
                      {availableLocationOptions.map((area) => (
                        <button
                          key={area}
                          type="button"
                          onClick={() => handleAreaChange(area)}
                          className={`rounded-full border px-3 py-2 text-sm font-medium transition-colors ${
                            selectedAreaParam !== 'all' && locationLabelToParam(area) === selectedAreaParam
                              ? 'border-brand-charcoal bg-brand-charcoal text-white'
                              : 'border-brand-charcoal/10 bg-white/78 text-brand-charcoal/62'
                          }`}
                        >
                          {area}
                        </button>
                      ))}
                    </>
                  ) : null}
                  {AMENITY_FILTER_CONFIG.map(({ key, label }) => {
                    const isActive = selectedAmenityFilters.includes(key);
                    const count = amenityFilterCounts[key];
                    if (count === 0 && !isActive) return null;

                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => handleAmenityToggle(key)}
                        className={`rounded-full border px-3 py-2 text-sm font-medium transition-colors ${
                          isActive
                            ? 'border-brand-charcoal bg-brand-charcoal text-white'
                            : 'border-brand-charcoal/10 bg-white/78 text-brand-charcoal/62'
                        }`}
                      >
                        {label}
                      </button>
                    );
                  })}
                  {hasActiveListingFilters ? (
                    <button
                      type="button"
                      onClick={clearAllListingFilters}
                      className="rounded-full border border-brand-charcoal/10 bg-white/78 px-3 py-2 text-sm font-semibold text-brand-charcoal/62"
                    >
                      Clear all
                    </button>
                  ) : null}
                </div>

                <div className="relative h-10 w-full max-w-[13rem] shrink-0">
                  <select
                    value={sortBy}
                    onChange={(event) => setSortBy(event.target.value as SortOption)}
                    className="h-10 w-full appearance-none rounded-[0.95rem] border border-brand-charcoal/10 bg-white/88 pl-3 pr-8 text-sm font-medium text-brand-charcoal/70 focus:outline-none cursor-pointer"
                  >
                    <option value="recommended">Sort: Recommended</option>
                    <option value="rating">Sort: Highest rated</option>
                    <option value="price-asc">Sort: Price low</option>
                    <option value="price-desc">Sort: Price high</option>
                  </select>
                  <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-brand-charcoal/40" />
                </div>
              </div>

              {secondaryFilterCount > 0 ? (
                <div className="flex items-center gap-2 overflow-x-auto pb-1 pr-1 no-scrollbar">
                  {!isAllCities ? <span className="rounded-full border border-brand-charcoal/10 bg-white/78 px-3 py-2 text-sm font-medium text-brand-charcoal/62">{cityName}</span> : null}
                  {selectedAreaParam !== 'all' ? <span className="rounded-full border border-brand-charcoal/10 bg-white/78 px-3 py-2 text-sm font-medium text-brand-charcoal/62">{selectedAreaLabel}</span> : null}
                  {verifiedOnly ? <span className="rounded-full border border-brand-sage/22 bg-brand-sage/10 px-3 py-2 text-sm font-medium text-brand-sage">Verified</span> : null}
                  {priceFilter !== 'all' ? <span className="rounded-full border border-brand-charcoal/10 bg-white/78 px-3 py-2 text-sm font-medium text-brand-charcoal/62">{PRICE_FILTER_LABELS[priceFilter as Exclude<PriceFilter, 'all'>]}</span> : null}
                  {selectedAmenityFilters.map((filter) => (
                    <span key={filter} className="rounded-full border border-brand-charcoal/10 bg-white/78 px-3 py-2 text-sm font-medium text-brand-charcoal/62">
                      {AMENITY_FILTER_CONFIG.find((item) => item.key === filter)?.label ?? filter}
                    </span>
                  ))}
                  {sortBy !== 'recommended' ? <span className="rounded-full border border-brand-charcoal/10 bg-white/78 px-3 py-2 text-sm font-medium text-brand-charcoal/62">{SORT_OPTION_LABELS[sortBy]}</span> : null}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      <div className="px-4 sm:px-6">
        <div className="site-shell lg:flex lg:items-start lg:gap-8">
          <div
            ref={listingsColumnRef}
            className={`${viewMode === 'map' && filteredVenues.length > 0 ? 'hidden lg:block' : ''} min-w-0 flex-1 pt-8 pb-24 lg:pt-10`}
          >
            {filteredVenues.length > 0 ? (
              <>
                {featuredVenues.length > 0 ? (
                  <div className="mb-10 rounded-[2rem] border border-[rgba(196,105,79,0.18)] bg-[linear-gradient(180deg,rgba(255,249,243,0.98),rgba(255,255,255,0.95))] p-6 shadow-[0_22px_60px_rgba(196,105,79,0.08)]">
                    <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
                      <div className="max-w-2xl">
                        <p className="mb-2 text-[10px] font-black uppercase tracking-[0.2em] text-brand-sage">Featured venues</p>
                        <h2 className="text-3xl text-brand-charcoal md:text-4xl">Priority placements on this page</h2>
                        <p className="mt-3 text-sm leading-relaxed text-brand-charcoal/58 md:text-base">
                          These venues have chosen premium visibility on this listing. They are still part of the same comparison flow, but surfaced first as featured partners.
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-5 md:grid-cols-2 items-stretch">
                      {featuredVenues.map((venue, i) => (
                        <div
                          key={venue.id}
                          className="h-full"
                          onMouseEnter={() => handleCardMouseEnter(venue.id)}
                          onMouseLeave={handleCardMouseLeave}
                        >
                          <VenueCard venue={venue} index={i} featured badgeLabel="Featured" />
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}

                {standardVenues.length > 0 ? (
                  <>
                    <div className="grid grid-cols-1 gap-5 md:grid-cols-2 items-stretch">
                      {standardVenues.slice(0, 6).map((venue, i) => (
                        <div
                          key={venue.id}
                          className="h-full"
                          onMouseEnter={() => handleCardMouseEnter(venue.id)}
                          onMouseLeave={handleCardMouseLeave}
                        >
                          <VenueCard venue={venue} index={i} />
                        </div>
                      ))}
                    </div>

                    <ListingNewsletterBlock />

                    {standardVenues.length > 6 && (
                      <div className="mt-5 grid grid-cols-1 gap-5 md:grid-cols-2 items-stretch">
                        {standardVenues.slice(6).map((venue, i) => (
                          <div
                            key={venue.id}
                            className="h-full"
                            onMouseEnter={() => handleCardMouseEnter(venue.id)}
                            onMouseLeave={handleCardMouseLeave}
                          >
                            <VenueCard venue={venue} index={i + 6} />
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <ListingNewsletterBlock />
                )}
              </>
            ) : !isLoading ? (
              <div className="py-24 text-center">
                <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-brand-charcoal/5">
                  <Search className="h-6 w-6 text-brand-charcoal/20" />
                </div>
                <h3 className="mb-2 text-2xl font-serif">No studios found</h3>
                <p className="mx-auto mb-8 max-w-md text-brand-charcoal/40">
                  {nearbyError
                    ? nearbyError
                    : isNearbySearchActive
                      ? `Nothing matched within ${nearbyRadius} miles of ${postcodeQuery.toUpperCase()}. Try widening the radius or browsing the main directory.`
                    : verifiedOnly
                    ? 'No verified venues here yet. Try turning off the verified filter.'
                    : isLondonPage && selectedAreaParam !== 'all'
                      ? `Nothing matched ${selectedAreaLabel} yet. Try another location or view all of London.`
                      : "We couldn't find any recovery studios here. Try expanding your search."}
                </p>
                {isNearbySearchActive ? (
                  <button
                    onClick={clearPostcodeSearch}
                    className="inline-flex items-center gap-2 rounded-xl bg-brand-charcoal px-6 py-3 text-sm font-medium text-white transition-opacity hover:opacity-90"
                  >
                    Reset nearby search
                  </button>
                ) : verifiedOnly ? (
                  <button
                    onClick={() => setVerifiedOnly(false)}
                    className="inline-flex items-center gap-2 rounded-xl bg-brand-charcoal px-6 py-3 text-sm font-medium text-white transition-opacity hover:opacity-90"
                  >
                    Show all venues
                  </button>
                ) : isLondonPage && selectedAreaParam !== 'all' ? (
                  <button
                    onClick={() => handleAreaChange('all')}
                    className="inline-flex items-center gap-2 rounded-xl bg-brand-charcoal px-6 py-3 text-sm font-medium text-white transition-opacity hover:opacity-90"
                  >
                    Show all London venues
                  </button>
                ) : (
                  <Link
                    to={buildDirectoryPath()}
                    className="inline-flex items-center gap-2 rounded-xl bg-brand-charcoal px-6 py-3 text-sm font-medium text-white transition-opacity hover:opacity-90"
                  >
                    Browse all venues
                  </Link>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2 items-stretch">
                {Array.from({ length: 6 }).map((_, index) => (
                  <ListingCardSkeleton key={index} />
                ))}
              </div>
            )}
          </div>

          <div
            className={`
              ${viewMode === 'map' ? 'block' : 'hidden'} lg:block
              pt-6 pb-[max(1rem,env(safe-area-inset-bottom))] lg:w-[38%] lg:self-start lg:pt-10 lg:pb-0 xl:w-[40%]
            `}
            style={{ minHeight: listingsColumnHeight || undefined }}
          >
            {filteredVenues.length > 0 ? (
              <div className="lg:sticky" style={{ top: desktopMapTop }}>
                <div
                  className="h-[68svh] overflow-hidden rounded-[1.5rem] bg-white/50 shadow-[0_12px_30px_rgba(26,26,26,0.05)] lg:h-[var(--listing-map-height)]"
                  style={{ ['--listing-map-height' as string]: desktopMapHeight }}
                >
                  {shouldRenderMap ? (
                    <React.Suspense
                      fallback={
                        <div className="h-full w-full animate-pulse bg-white/60 backdrop-blur-sm" />
                      }
                    >
                      <LazyVenueMap venues={filteredVenues} highlightedId={hoveredId} />
                    </React.Suspense>
                  ) : (
                    <div className="h-full w-full animate-pulse bg-white/60 backdrop-blur-sm" />
                  )}
                </div>

                {therapies.filter((t) => t.slug !== therapy).length > 0 && (
                  <div className="mt-4 flex flex-nowrap gap-2 overflow-x-auto pb-1 no-scrollbar">
                    {therapies.filter((t) => t.slug !== therapy).slice(0, 6).map((t) => (
                      <Link
                        key={t.slug}
                        to={buildListingPath(isAllCities ? 'all' : city, t.slug)}
                        className="flex-shrink-0 rounded-full border border-brand-charcoal/10 bg-white px-4 py-2 text-xs font-medium text-brand-charcoal/68 transition-all hover:border-brand-charcoal/20 hover:text-brand-charcoal"
                      >
                        {t.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ) : isLoading ? (
              <div className="lg:sticky" style={{ top: desktopMapTop }}>
                <div
                  className="h-[68svh] animate-pulse overflow-hidden rounded-[1.5rem] bg-white/60 shadow-[0_12px_30px_rgba(26,26,26,0.05)] lg:h-[var(--listing-map-height)]"
                  style={{ ['--listing-map-height' as string]: desktopMapHeight }}
                />
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};
